import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ScrollArea } from '../ui/scroll-area';
import { Camera, History, Wand2 } from 'lucide-react';
import { ownershipOptions, ratePeriods, isBorrowed } from './ownership';
import { useTranslation } from 'react-i18next';
import NameplateScanDialog from './NameplateScanDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const today = () => new Date().toISOString().split('T')[0];

const VAT_RATES = [
  { label: '5%', value: 5 }, { label: '8%', value: 8 },
  { label: '15%', value: 15 }, { label: '20%', value: 20 },
  { label: '23%', value: 23 }, { label: null, value: null },
];

const EMPTY_RENTAL = {
  supplier_id: '', order_reference: '', rate: '', rate_period: 'day',
  currency: 'PLN', start_date: '', end_date: '', off_hire_date: '',
};

const EMPTY_FORM = {
  name: '', asset_type: 'tangible', category_id: '', manufacturer: '',
  model_id: '',
  storage_location_id: '', delivery_id: '', part_number: '', serial_number: '', manual_link: '',
  manufacture_date: '', registered_date: today(), description: '',
  ownership: 'own', owner_customer_id: '', provided_for_project_id: '',
  expected_return_date: '', returned_date: '', condition_on_receipt: '',
  rental: { ...EMPTY_RENTAL },
  supplier_id: '', shop_link: '', purchase_date: '', purchase_currency: 'PLN',
  purchase_value: '', vat_rate: '', vat_value: '', warranty_date: '',
  condition: 'New', condition_id: '',
  inspection_date: '', next_inspection_date: '', replace_by_date: '',
  pat_test_date: '', next_pat_test_date: '',
  vehicle_info: { registration_plate: '', vin_number: '', mot_date: '', next_mot_date: '' },
  insurance: { insurance_date: '', insurance_valid_date: '', contact_name: '', contact_email: '', contact_phone: '' },
  depreciation_method: '', depreciation_rate: '', useful_life_years: '', residual_value: '',
};

const populateForm = (asset) => ({
  name: asset.name || '', asset_type: asset.asset_type || 'tangible',
  category_id: asset.category_id || '', manufacturer: asset.manufacturer || '',
  model_id: asset.model_id || '',
  part_number: asset.part_number || '', serial_number: asset.serial_number || '',
  manual_link: asset.manual_link || '',
  manufacture_date: asset.manufacture_date || '', registered_date: asset.registered_date || '',
  description: asset.description || '',
  ownership: asset.ownership || 'own',
  owner_customer_id: asset.owner_customer_id || '',
  provided_for_project_id: asset.provided_for_project_id || '',
  expected_return_date: asset.expected_return_date || '',
  returned_date: asset.returned_date || '',
  condition_on_receipt: asset.condition_on_receipt || '',
  rental: { ...EMPTY_RENTAL, ...(asset.rental || {}), rate: asset.rental?.rate ?? '' },
  supplier_id: asset.supplier_id || '', shop_link: asset.shop_link || '',
  purchase_date: asset.purchase_date || '', purchase_currency: asset.purchase_currency || 'PLN',
  purchase_value: asset.purchase_value ?? '', vat_rate: '', vat_value: asset.vat_value ?? '',
  warranty_date: asset.warranty_date || '',
  condition: asset.condition || 'New', condition_id: asset.condition_id || '',
  inspection_date: asset.inspection_date || '', next_inspection_date: asset.next_inspection_date || '',
  replace_by_date: asset.replace_by_date || '',
  pat_test_date: asset.pat_test_date || '', next_pat_test_date: asset.next_pat_test_date || '',
  vehicle_info: {
    registration_plate: asset.vehicle_info?.registration_plate || '',
    vin_number: asset.vehicle_info?.vin_number || '',
    mot_date: asset.vehicle_info?.mot_date || '',
    next_mot_date: asset.vehicle_info?.next_mot_date || '',
  },
  insurance: {
    insurance_date: asset.insurance?.insurance_date || '',
    insurance_valid_date: asset.insurance?.insurance_valid_date || '',
    contact_name: asset.insurance?.contact_name || '',
    contact_email: asset.insurance?.contact_email || '',
    contact_phone: asset.insurance?.contact_phone || '',
  },
  depreciation_method: asset.depreciation_method || '',
  depreciation_rate: asset.depreciation_rate ?? '',
  useful_life_years: asset.useful_life_years ?? '',
  residual_value: asset.residual_value ?? '',
});

const Field = ({ label, children, span }) => (
  <div className={span === 2 ? 'sm:col-span-2' : ''}>
    <Label className="text-xs text-muted-foreground">{label}</Label>
    {children}
  </div>
);

const addMonths = (months) => {
  const d = new Date();
  d.setMonth(d.getMonth() + Number(months));
  return d.toISOString().slice(0, 10);
};

/** Everything a scanned nameplate can contribute to a NEW asset: the
 *  catalogue defaults for the tool type, plus the facts that belong to this
 *  one unit — its serial and the year it was built. */
const fromScan = (model, plate = {}, deliveryId = null) => {
  const r = plate.reviewed || {};
  const manufacturer = model.manufacturer || r.manufacturer || '';
  const interval = model.inspection_interval_months || r.inspection_interval_months;
  const patRequired = model.pat_required ?? r.pat_required;
  return {
    ...EMPTY_FORM,
    model_id: model.id,
    name: [manufacturer, model.name].filter(Boolean).join(' '),
    manufacturer,
    category_id: model.category_id || r.category_id || '',
    asset_type: model.asset_type || 'tangible',
    part_number: model.part_number || r.part_number || '',
    manual_link: model.manual_link || r.manual_link || '',
    serial_number: plate.serial_number || '',
    delivery_id: deliveryId || '',
    // A plate gives a year, sometimes a month — never a day. Anchor to
    // January so the field is valid and obviously approximate.
    manufacture_date: /^\d{4}$/.test(String(plate.year_of_manufacture || ''))
      ? `${plate.year_of_manufacture}-01-01`
      : '',
    useful_life_years: model.useful_life_years ?? '',
    next_inspection_date: interval ? addMonths(interval) : '',
    next_pat_test_date: patRequired ? addMonths(interval || 12) : '',
  };
};

/**
 * Fields carried over from the last tool you registered.
 *
 * Registering a delivery means ten near-identical forms: same condition, same
 * ownership, same supplier, same delivery date. These are remembered so each
 * subsequent tool only needs its own name and serial. Deliberately NOT
 * remembered: anything unique to one unit (name, serial, plate year) or
 * anything that would silently misstate money (purchase value, VAT).
 *
 * There is no location field on an asset — where a tool is comes from its
 * assignment, so that is not something this can carry.
 */
const STICKY_FIELDS = [
  'condition_id', 'condition',
  'ownership', 'owner_customer_id', 'provided_for_project_id',
  'supplier_id', 'purchase_date', 'purchase_currency',
  'storage_location_id', 'delivery_id',
];
const STICKY_KEY = 'inv.assetDefaults.v1';

const readSticky = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(STICKY_KEY) || 'null');
    if (!raw) return null;
    const kept = Object.fromEntries(
      STICKY_FIELDS.filter((k) => raw[k] !== undefined && raw[k] !== '' && raw[k] !== null)
        .map((k) => [k, raw[k]]),
    );
    return Object.keys(kept).length ? kept : null;
  } catch { return null; }
};

const writeSticky = (form) => {
  try {
    localStorage.setItem(STICKY_KEY, JSON.stringify(
      Object.fromEntries(STICKY_FIELDS.map((k) => [k, form[k]])),
    ));
  } catch { /* private browsing — carrying over is a convenience, not a feature to fail on */ }
};

const AssetForm = ({ open, onClose, onSaved, asset, categories, conditions, suppliers, config = {}, prefill = null }) => {
  const { t } = useTranslation();
  const { manufacturers = [], currencies = [], models = [], customers = [], projects = [],
    storageLocations = [] } = config;
  const currencyList = currencies.length > 0 ? currencies : ['PLN', 'EUR', 'GBP', 'USD'];

  const [form, setForm] = useState(asset ? populateForm(asset) : { ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [formTab, setFormTab] = useState('general');
  const [scanOpen, setScanOpen] = useState(false);
  // The plate photo is already in storage from the scan; it gets attached to
  // the asset once the asset exists and therefore has something to attach to.
  const [platePhoto, setPlatePhoto] = useState(null);
  // What was carried over from the last tool registered, so the form can say so.
  const [carried, setCarried] = useState(null);
  const [deliveries, setDeliveries] = useState([]);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/inventory/deliveries`).then((r) => setDeliveries(r.data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (asset) {
      setForm(populateForm(asset));
      setPlatePhoto(null);
      setCarried(null);
    } else {
      // Anything read off the plate wins; the carry-over only fills the rest.
      const sticky = readSticky();
      const base = prefill?.model ? fromScan(prefill.model, prefill.plate, prefill.delivery_id) : { ...EMPTY_FORM };
      // The batch being booked into is not negotiable, so it wins over both
      // the carry-over and whatever was last used.
      const merged = sticky ? { ...base, ...sticky } : base;
      if (prefill?.delivery_id) merged.delivery_id = prefill.delivery_id;
      setForm(merged);
      setPlatePhoto(prefill?.plate?.plate_photo_gridfs_id || null);
      setCarried(sticky);
    }
    setFormTab('general');
  }, [asset, prefill]);

  const clearCarried = () => {
    try { localStorage.removeItem(STICKY_KEY); } catch { /* nothing to clear */ }
    const base = prefill?.model ? fromScan(prefill.model, prefill.plate, prefill.delivery_id) : { ...EMPTY_FORM };
    setForm(base);
    setCarried(null);
  };

  /** Plain-English list of what was carried over, for the banner. Values that
   *  merely match the form's own defaults are left out — announcing
   *  "Ownership: own" on the first reuse is noise, not information. */
  const carriedLabels = () => {
    if (!carried) return [];
    const names = [];
    if (carried.condition_id || carried.condition) names.push(t('inv.common.condition'));
    if (carried.ownership && carried.ownership !== 'own') names.push(t('inv.detail.ownership'));
    if (carried.owner_customer_id) names.push(t('inv.form.ownerCustomer'));
    if (carried.supplier_id) names.push(t('inv.form.supplier'));
    if (carried.purchase_date) names.push(t('inv.form.purchaseDate'));
    if (carried.storage_location_id) names.push(t('inv.form.storageLocation'));
    if (carried.delivery_id) names.push(t('inv.form.delivery'));
    return names;
  };
  const carriedNames = carriedLabels();

  const set = (field, val) => setForm((f) => ({ ...f, [field]: val }));
  const setVehicle = (field, val) => setForm((f) => ({ ...f, vehicle_info: { ...f.vehicle_info, [field]: val } }));
  const setInsurance = (field, val) => setForm((f) => ({ ...f, insurance: { ...f.insurance, [field]: val } }));
  const setRental = (field, val) => setForm((f) => ({ ...f, rental: { ...f.rental, [field]: val } }));

  // Picking a model fills in everything the catalogue already knows, so a
  // fresh item needs a serial number and nothing else.
  const applyModel = (modelId) => {
    const m = models.find((x) => x.id === modelId);
    if (!m) { set('model_id', modelId); return; }
    setForm((f) => ({
      ...f,
      model_id: modelId,
      name: f.name?.trim() ? f.name : [m.manufacturer, m.name].filter(Boolean).join(' '),
      manufacturer: m.manufacturer || f.manufacturer,
      category_id: m.category_id || f.category_id,
      asset_type: m.asset_type || f.asset_type,
      part_number: m.part_number || f.part_number,
      manual_link: m.manual_link || f.manual_link,
      useful_life_years: m.useful_life_years ?? f.useful_life_years,
      next_inspection_date: m.inspection_interval_months
        ? addMonths(m.inspection_interval_months)
        : f.next_inspection_date,
      next_pat_test_date: m.pat_required && !f.next_pat_test_date
        ? addMonths(m.inspection_interval_months || 12)
        : f.next_pat_test_date,
    }));
    toast.success(t('inv.form.defaultsApplied', { name: m.name }));
  };

  const handleVatRate = (rate) => {
    set('vat_rate', rate);
    if (rate && form.purchase_value) {
      set('vat_value', (parseFloat(form.purchase_value) * rate / 100).toFixed(2));
    }
  };
  const handleNetChange = (val) => {
    set('purchase_value', val);
    if (form.vat_rate && val) {
      set('vat_value', (parseFloat(val) * parseFloat(form.vat_rate) / 100).toFixed(2));
    }
  };
  const grossValue = form.purchase_value && form.vat_value
    ? (parseFloat(form.purchase_value) + parseFloat(form.vat_value)).toFixed(2)
    : '';

  const handleSubmit = async (e, thenScanNext = false) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error(t('inv.form.nameRequired')); return; }
    if (form.ownership === 'customer_provided' && !form.owner_customer_id) {
      toast.error(t('inv.form.ownerRequired'));
      setFormTab('ownership');
      return;
    }
    setSaving(true);
    try {
      const rentalFilled = Object.entries(form.rental)
        .some(([k, v]) => v !== '' && v !== null && !['rate_period', 'currency'].includes(k));
      const payload = {
        ...form,
        purchase_value: form.purchase_value === '' ? null : parseFloat(form.purchase_value),
        vat_value: form.vat_value === '' ? null : parseFloat(form.vat_value),
        depreciation_rate: form.depreciation_rate === '' ? null : parseFloat(form.depreciation_rate),
        useful_life_years: form.useful_life_years === '' ? null : parseFloat(form.useful_life_years),
        residual_value: form.residual_value === '' ? null : parseFloat(form.residual_value),
        vehicle_info: Object.values(form.vehicle_info).some(v => v) ? form.vehicle_info : null,
        insurance: Object.values(form.insurance).some(v => v) ? form.insurance : null,
        rental: form.ownership === 'rented' && rentalFilled ? {
          ...form.rental,
          supplier_id: form.rental.supplier_id || null,
          rate: form.rental.rate === '' ? null : parseFloat(form.rental.rate),
        } : null,
        depreciation_method: form.depreciation_method || null,
        category_id: form.category_id || null,
        condition_id: form.condition_id || null,
        supplier_id: form.supplier_id || null,
        model_id: form.model_id || null,
        owner_customer_id: form.owner_customer_id || null,
        provided_for_project_id: form.provided_for_project_id || null,
      };
      delete payload.vat_rate;
      if (asset) { await axios.put(`${API}/inventory/assets/${asset.id}`, payload); toast.success(t('inv.form.updated')); }
      else {
        const created = await axios.post(`${API}/inventory/assets`, payload);
        if (platePhoto) {
          try {
            await axios.post(`${API}/inventory/assets/${created.data.id}/pictures/adopt`, { gridfs_id: platePhoto });
          } catch { toast.warning(t('inv.nameplate.platePhotoFailed')); }
        }
        // Remember the shared details so the next tool off the same delivery
        // needs only its name and serial.
        writeSticky(form);
        toast.success(t('inv.form.created'));
      }
      onSaved(thenScanNext ? { scanNext: true } : undefined);
    } catch (err) { toast.error(err.response?.data?.detail || t('inv.form.saveFailed')); }
    setSaving(false);
  };

  const isVehicle = categories.find(c => c.id === form.category_id)?.name === 'Vehicles';
  const borrowed = isBorrowed(form.ownership);
  const modelsForManufacturer = form.manufacturer
    ? models.filter((m) => !m.manufacturer || m.manufacturer === form.manufacturer)
    : models;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh]" data-testid="asset-form-dialog">
        <DialogHeader><DialogTitle>{asset ? t('inv.form.editAsset') : t('inv.assets.newAsset')}</DialogTitle></DialogHeader>
        {/* Sticky defaults are a trap if they are invisible, so say what was
            carried over and make undoing it one tap. */}
        {carried && carriedNames.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900"
            data-testid="asset-carried-over-banner">
            <History className="h-3.5 w-3.5 shrink-0" />
            <span>{t('inv.form.carriedOver', { fields: carriedNames.join(', ') })}</span>
            <button type="button" onClick={clearCarried}
              className="ml-auto font-medium underline underline-offset-2 hover:no-underline"
              data-testid="asset-clear-carried-btn">
              {t('inv.form.clearCarried')}
            </button>
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <Tabs value={formTab} onValueChange={setFormTab}>
            <TabsList className="mb-3 flex h-auto w-full flex-wrap justify-start gap-1">
              <TabsTrigger value="general" className="px-2 text-xs" data-testid="tab-general">{t('inv.detail.general')}</TabsTrigger>
              <TabsTrigger value="ownership" className="px-2 text-xs" data-testid="tab-ownership">{t('inv.detail.ownership')}</TabsTrigger>
              <TabsTrigger value="purchase" className="px-2 text-xs" data-testid="tab-purchase">{t('inv.form.tabs.purchase')}</TabsTrigger>
              <TabsTrigger value="condition" className="px-2 text-xs" data-testid="tab-condition">{t('inv.common.condition')}</TabsTrigger>
              {isVehicle && <TabsTrigger value="vehicle" className="px-2 text-xs" data-testid="tab-vehicle">{t('inv.detail.vehicle')}</TabsTrigger>}
              <TabsTrigger value="insurance" className="px-2 text-xs" data-testid="tab-insurance">{t('inv.detail.insurance')}</TabsTrigger>
              <TabsTrigger value="financial" className="px-2 text-xs" data-testid="tab-financial">{t('inv.detail.financial')}</TabsTrigger>
            </TabsList>
            <ScrollArea className="h-[50vh] pr-3">
              <TabsContent value="general" className="mt-0">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label={t('inv.form.name')} span={2}><Input value={form.name} onChange={(e) => set('name', e.target.value)} required data-testid="asset-name-input" /></Field>
                  <Field label={t('inv.form.manufacturer')}>
                    {manufacturers.length > 0 ? (
                      <Select value={form.manufacturer} onValueChange={(v) => set('manufacturer', v)}>
                        <SelectTrigger data-testid="asset-manufacturer-input"><SelectValue placeholder={t('inv.form.select')} /></SelectTrigger>
                        <SelectContent>
                          {/* A manufacturer read off a nameplate is often not in
                              the register yet. Offer it anyway, or the value
                              sits in state while the field looks empty. */}
                          {form.manufacturer && !manufacturers.some((m) => m.name === form.manufacturer) && (
                            <SelectItem value={form.manufacturer}>{form.manufacturer}</SelectItem>
                          )}
                          {manufacturers.map((m) => (<SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input value={form.manufacturer} onChange={(e) => set('manufacturer', e.target.value)} data-testid="asset-manufacturer-input" />
                    )}
                  </Field>
                  <Field label={t('inv.form.modelFromTheRegister')}>
                    <div className="flex gap-1">
                      <Select value={form.model_id} onValueChange={applyModel}>
                        <SelectTrigger data-testid="asset-model-select"><SelectValue placeholder={t('inv.form.pickToAutoFill')} /></SelectTrigger>
                        <SelectContent>
                          {modelsForManufacturer.length === 0 ? (
                            <div className="px-2 py-3 text-xs text-muted-foreground">
                              {t('inv.form.noModelsYet')}
                            </div>
                          ) : modelsForManufacturer.map((m) => (
                            <SelectItem key={m.id} value={m.id}>
                              {[m.manufacturer, m.name].filter(Boolean).join(' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {form.model_id && (
                        <Button type="button" variant="outline" size="icon" title={t('inv.form.reapplyDefaults')}
                          onClick={() => applyModel(form.model_id)} data-testid="reapply-model-btn">
                          <Wand2 className="h-4 w-4" />
                        </Button>
                      )}
                      <Button type="button" variant="outline" size="icon" title={t('inv.nameplate.scanButton')}
                        onClick={() => setScanOpen(true)} data-testid="asset-form-scan-nameplate-btn">
                        <Camera className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      {t('inv.nameplate.formHint')}
                    </p>
                  </Field>
                  <Field label={t('inv.form.assetType')}>
                    <Select value={form.asset_type} onValueChange={(v) => set('asset_type', v)}>
                      <SelectTrigger data-testid="asset-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="tangible">{t('inv.assets.type.tangible')}</SelectItem><SelectItem value="intangible">{t('inv.assets.type.intangible')}</SelectItem></SelectContent>
                    </Select>
                  </Field>
                  <Field label={t('inv.form.category')}>
                    <Select value={form.category_id} onValueChange={(v) => set('category_id', v)}>
                      <SelectTrigger data-testid="asset-category-select"><SelectValue placeholder={t('inv.form.select')} /></SelectTrigger>
                      <SelectContent>{categories.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
                    </Select>
                  </Field>
                  <Field label={t('inv.form.partNumber')}><Input value={form.part_number} onChange={(e) => set('part_number', e.target.value)} /></Field>
                  <Field label={t('inv.form.serialNumber')}><Input value={form.serial_number} onChange={(e) => set('serial_number', e.target.value)} data-testid="asset-serial-input" /></Field>
                  <Field label={t('inv.form.manualLink')}><Input value={form.manual_link} onChange={(e) => set('manual_link', e.target.value)} placeholder={t('auto.inv.https')} /></Field>
                  <Field label={t('inv.form.manufactureDate')}><Input type="date" value={form.manufacture_date} onChange={(e) => set('manufacture_date', e.target.value)} /></Field>
                  <Field label={t('inv.form.registeredDate')}><Input type="date" value={form.registered_date} onChange={(e) => set('registered_date', e.target.value)} data-testid="asset-registered-date" /></Field>
                  <Field label={t('inv.form.description')} span={2}><Textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={2} data-testid="asset-description-input" /></Field>
                </div>
              </TabsContent>

              <TabsContent value="ownership" className="mt-0">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label={t('inv.form.heldAs')} span={2}>
                    <Select value={form.ownership} onValueChange={(v) => set('ownership', v)}>
                      <SelectTrigger data-testid="asset-ownership-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ownershipOptions(t).map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  {form.ownership === 'own' && (
                    <p className="sm:col-span-2 rounded-md bg-muted/60 p-3 text-xs text-muted-foreground">
                      {t('inv.form.ownKitNote')}
                    </p>
                  )}

                  {form.ownership === 'customer_provided' && (
                    <>
                      <Field label={t('inv.form.ownerCustomer')}>
                        <Select value={form.owner_customer_id} onValueChange={(v) => set('owner_customer_id', v)}>
                          <SelectTrigger data-testid="asset-owner-customer-select"><SelectValue placeholder={t('inv.form.selectCustomer')} /></SelectTrigger>
                          <SelectContent>
                            {customers.map((c) => (
                              <SelectItem key={c.id} value={c.id}>{c.company_name || c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label={t('inv.form.providedForProject')}>
                        <Select value={form.provided_for_project_id} onValueChange={(v) => set('provided_for_project_id', v)}>
                          <SelectTrigger data-testid="asset-provided-project-select"><SelectValue placeholder={t('inv.form.selectProject')} /></SelectTrigger>
                          <SelectContent>
                            {projects.map((p) => (
                              <SelectItem key={p.id} value={p.id}>
                                {[p.project_number, p.name].filter(Boolean).join(' — ')}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                    </>
                  )}

                  {form.ownership === 'rented' && (
                    <>
                      <Field label={t('inv.form.hireCompany')}>
                        <Select value={form.rental.supplier_id} onValueChange={(v) => setRental('supplier_id', v)}>
                          <SelectTrigger data-testid="asset-rental-supplier-select"><SelectValue placeholder={t('inv.form.selectSupplier')} /></SelectTrigger>
                          <SelectContent>{suppliers.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}</SelectContent>
                        </Select>
                      </Field>
                      <Field label={t('inv.form.hireOrderContractRef')}>
                        <Input value={form.rental.order_reference} onChange={(e) => setRental('order_reference', e.target.value)}
                          data-testid="asset-rental-ref" />
                      </Field>
                      <Field label={t('inv.form.rate')}>
                        <Input type="number" step="0.01" value={form.rental.rate}
                          onChange={(e) => setRental('rate', e.target.value)} data-testid="asset-rental-rate" />
                      </Field>
                      <Field label={t('inv.form.per')}>
                        <Select value={form.rental.rate_period} onValueChange={(v) => setRental('rate_period', v)}>
                          <SelectTrigger data-testid="asset-rental-period"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {ratePeriods(t).map((p) => (<SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>))}
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label={t('inv.form.currency')}>
                        <Select value={form.rental.currency} onValueChange={(v) => setRental('currency', v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{currencyList.map(c => (<SelectItem key={c} value={c}>{c}</SelectItem>))}</SelectContent>
                        </Select>
                      </Field>
                      <Field label={t('inv.form.providedForProject')}>
                        <Select value={form.provided_for_project_id} onValueChange={(v) => set('provided_for_project_id', v)}>
                          <SelectTrigger data-testid="asset-rental-project-select"><SelectValue placeholder={t('inv.form.selectProject')} /></SelectTrigger>
                          <SelectContent>
                            {projects.map((p) => (
                              <SelectItem key={p.id} value={p.id}>
                                {[p.project_number, p.name].filter(Boolean).join(' — ')}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label={t('inv.form.hireFrom')}><Input type="date" value={form.rental.start_date} onChange={(e) => setRental('start_date', e.target.value)} /></Field>
                      <Field label={t('inv.form.hireUntil')}><Input type="date" value={form.rental.end_date} onChange={(e) => setRental('end_date', e.target.value)} /></Field>
                      <Field label={t('inv.form.offHiredOn')} span={2}>
                        <Input type="date" value={form.rental.off_hire_date} onChange={(e) => setRental('off_hire_date', e.target.value)}
                          data-testid="asset-off-hire-date" />
                      </Field>
                    </>
                  )}

                  {borrowed && (
                    <>
                      <Field label={t('inv.form.returnExpectedBy')}>
                        <Input type="date" value={form.expected_return_date}
                          onChange={(e) => set('expected_return_date', e.target.value)}
                          data-testid="asset-expected-return" />
                      </Field>
                      <Field label={t('inv.form.actuallyReturnedOn')}>
                        <Input type="date" value={form.returned_date}
                          onChange={(e) => set('returned_date', e.target.value)}
                          data-testid="asset-returned-date" />
                      </Field>
                      <Field label={t('inv.form.conditionOnReceipt')} span={2}>
                        {conditions.length > 0 ? (
                          <Select value={form.condition_on_receipt} onValueChange={(v) => set('condition_on_receipt', v)}>
                            <SelectTrigger data-testid="asset-condition-on-receipt"><SelectValue placeholder={t('inv.form.select')} /></SelectTrigger>
                            <SelectContent>{conditions.map((c) => (<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}</SelectContent>
                          </Select>
                        ) : (
                          <Input value={form.condition_on_receipt} onChange={(e) => set('condition_on_receipt', e.target.value)}
                            data-testid="asset-condition-on-receipt" />
                        )}
                      </Field>
                      <p className="sm:col-span-2 rounded-md bg-amber-50 p-3 text-xs text-amber-900">
                        {t('inv.form.handoverNote')}
                      </p>
                    </>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="purchase" className="mt-0">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label={t('inv.form.supplier')}>
                    <Select value={form.supplier_id} onValueChange={(v) => set('supplier_id', v)}>
                      <SelectTrigger data-testid="asset-supplier-select"><SelectValue placeholder={t('inv.form.select')} /></SelectTrigger>
                      <SelectContent>{suppliers.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}</SelectContent>
                    </Select>
                  </Field>
                  <Field label={t('inv.form.shopLink')}><Input value={form.shop_link} onChange={(e) => set('shop_link', e.target.value)} placeholder={t('auto.inv.https')} /></Field>
                  <Field label={t('inv.form.purchaseDate')}><Input type="date" value={form.purchase_date} onChange={(e) => set('purchase_date', e.target.value)} data-testid="asset-purchase-date" /></Field>
                  <Field label={t('inv.form.currency')}>
                    <Select value={form.purchase_currency} onValueChange={(v) => set('purchase_currency', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{currencyList.map(c => (<SelectItem key={c} value={c}>{c}</SelectItem>))}</SelectContent>
                    </Select>
                  </Field>
                  <Field label={t('inv.form.netValue')}>
                    <Input type="number" step="0.01" value={form.purchase_value} onChange={(e) => handleNetChange(e.target.value)} data-testid="asset-purchase-value" />
                  </Field>
                  <Field label={t('inv.form.vatRate')}>
                    <Select value={form.vat_rate ? String(form.vat_rate) : ''} onValueChange={(v) => handleVatRate(v === 'manual' ? '' : parseFloat(v))}>
                      <SelectTrigger data-testid="asset-vat-rate"><SelectValue placeholder={t('inv.form.selectRate')} /></SelectTrigger>
                      <SelectContent>
                        {VAT_RATES.map(r => (<SelectItem key={r.label || 'manual'} value={r.value === null ? 'manual' : String(r.value)}>{r.label || t('inv.form.manualRate')}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label={t('inv.form.vatValue')}>
                    <Input type="number" step="0.01" value={form.vat_value} onChange={(e) => set('vat_value', e.target.value)} />
                  </Field>
                  <Field label={t('inv.form.grossValue')}>
                    <Input type="number" step="0.01" value={grossValue} disabled className="bg-muted" data-testid="asset-gross-value" />
                  </Field>
                  <Field label={t('inv.form.warrantyExpiry')} span={2}><Input type="date" value={form.warranty_date} onChange={(e) => set('warranty_date', e.target.value)} data-testid="asset-warranty-date" /></Field>
                  {/* Where it lives and which delivery brought it. Both are
                      carried over, so a whole batch needs setting once. */}
                  <Field label={t('inv.form.storageLocation')}>
                    <Select value={form.storage_location_id} onValueChange={(v) => set('storage_location_id', v)}>
                      <SelectTrigger data-testid="asset-storage-location-select"><SelectValue placeholder={t('inv.form.select')} /></SelectTrigger>
                      <SelectContent>
                        {storageLocations.map((l) => (<SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label={t('inv.form.delivery')}>
                    <Select value={form.delivery_id} onValueChange={(v) => set('delivery_id', v)}>
                      <SelectTrigger data-testid="asset-delivery-select"><SelectValue placeholder={t('inv.form.select')} /></SelectTrigger>
                      <SelectContent>
                        {deliveries.map((d) => (
                          <SelectItem key={d.id} value={d.id}>
                            {[d.reference, d.supplier_name, d.delivery_date].filter(Boolean).join(' · ')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
              </TabsContent>

              <TabsContent value="condition" className="mt-0">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label={t('inv.form.condition')}>
                    <Select value={form.condition} onValueChange={(v) => { set('condition', v); const m = conditions.find(c => c.name === v); if (m) set('condition_id', m.id); }}>
                      <SelectTrigger data-testid="asset-condition-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{conditions.map((c) => (<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}</SelectContent>
                    </Select>
                  </Field>
                  <Field label={t('inv.form.replaceByDate')}><Input type="date" value={form.replace_by_date} onChange={(e) => set('replace_by_date', e.target.value)} /></Field>
                  <Field label={t('inv.form.lastInspection')}><Input type="date" value={form.inspection_date} onChange={(e) => set('inspection_date', e.target.value)} /></Field>
                  <Field label={t('inv.form.nextInspection')}><Input type="date" value={form.next_inspection_date} onChange={(e) => set('next_inspection_date', e.target.value)} /></Field>
                  <Field label={t('inv.form.lastPatTest')}><Input type="date" value={form.pat_test_date} onChange={(e) => set('pat_test_date', e.target.value)} /></Field>
                  <Field label={t('inv.form.nextPatTest')}><Input type="date" value={form.next_pat_test_date} onChange={(e) => set('next_pat_test_date', e.target.value)} /></Field>
                </div>
              </TabsContent>

              {isVehicle && (
                <TabsContent value="vehicle" className="mt-0">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label={t('inv.form.registrationPlate')}><Input value={form.vehicle_info.registration_plate} onChange={(e) => setVehicle('registration_plate', e.target.value)} data-testid="vehicle-reg-plate" /></Field>
                    <Field label={t('inv.form.vinNumber')}><Input value={form.vehicle_info.vin_number} onChange={(e) => setVehicle('vin_number', e.target.value)} data-testid="vehicle-vin" /></Field>
                    <Field label={t('inv.form.motDate')}><Input type="date" value={form.vehicle_info.mot_date} onChange={(e) => setVehicle('mot_date', e.target.value)} /></Field>
                    <Field label={t('inv.form.nextMotDate')}><Input type="date" value={form.vehicle_info.next_mot_date} onChange={(e) => setVehicle('next_mot_date', e.target.value)} /></Field>
                  </div>
                </TabsContent>
              )}

              <TabsContent value="insurance" className="mt-0">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label={t('inv.form.insuranceStart')}><Input type="date" value={form.insurance.insurance_date} onChange={(e) => setInsurance('insurance_date', e.target.value)} /></Field>
                  <Field label={t('inv.form.insuranceValidUntil')}><Input type="date" value={form.insurance.insurance_valid_date} onChange={(e) => setInsurance('insurance_valid_date', e.target.value)} /></Field>
                  <Field label={t('inv.form.contactName')}><Input value={form.insurance.contact_name} onChange={(e) => setInsurance('contact_name', e.target.value)} /></Field>
                  <Field label={t('inv.form.contactEmail')}><Input type="email" value={form.insurance.contact_email} onChange={(e) => setInsurance('contact_email', e.target.value)} /></Field>
                  <Field label={t('inv.form.contactPhone')} span={2}><Input value={form.insurance.contact_phone} onChange={(e) => setInsurance('contact_phone', e.target.value)} /></Field>
                </div>
              </TabsContent>

              <TabsContent value="financial" className="mt-0">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label={t('inv.form.depreciationMethod')}>
                    <Select value={form.depreciation_method} onValueChange={(v) => set('depreciation_method', v)}>
                      <SelectTrigger data-testid="asset-depreciation-method"><SelectValue placeholder={t('inv.form.none')} /></SelectTrigger>
                      <SelectContent><SelectItem value="straight_line">{t('inv.depreciation.method.straightLine')}</SelectItem><SelectItem value="reducing_balance">{t('inv.depreciation.method.reducingBalance')}</SelectItem></SelectContent>
                    </Select>
                  </Field>
                  <Field label={t('inv.form.rate')}><Input type="number" step="0.1" value={form.depreciation_rate} onChange={(e) => set('depreciation_rate', e.target.value)} disabled={form.depreciation_method !== 'reducing_balance'} /></Field>
                  <Field label={t('inv.form.usefulLifeYears')}><Input type="number" step="0.5" value={form.useful_life_years} onChange={(e) => set('useful_life_years', e.target.value)} disabled={form.depreciation_method !== 'straight_line'} /></Field>
                  <Field label={t('inv.form.residualValue')}><Input type="number" step="0.01" value={form.residual_value} onChange={(e) => set('residual_value', e.target.value)} disabled={!form.depreciation_method} /></Field>
                </div>
              </TabsContent>
            </ScrollArea>
          </Tabs>
          <div className="flex flex-wrap justify-end gap-2 mt-4 pt-3 border-t">
            <Button type="button" variant="outline" onClick={onClose} data-testid="cancel-asset-form">{t('common.cancel')}</Button>
            {/* Booking in a delivery: save this one and go straight back to the
                camera, keeping the shared details. */}
            {!asset && (
              <Button type="button" variant="secondary" disabled={saving}
                onClick={(e) => handleSubmit(e, true)} data-testid="save-and-scan-next-btn">
                <Camera className="w-4 h-4 mr-1" /> {t('inv.form.saveAndScanNext')}
              </Button>
            )}
            <Button type="submit" disabled={saving} data-testid="save-asset-btn">{saving ? t('inv.common.saving') : asset ? t('inv.common.update') : t('inv.common.create')}</Button>
          </div>
        </form>

        {/* Scan a plate straight into the form: the catalogue entry is created
            and selected here, and the serial off the plate lands on the asset. */}
        <NameplateScanDialog
          open={scanOpen}
          onOpenChange={setScanOpen}
          categories={categories}
          mode="asset"
          onSaved={() => config.onModelsChange?.()}
          onReadyForAsset={({ model, plate }) => {
            const r = plate?.reviewed || {};
            setPlatePhoto(plate?.plate_photo_gridfs_id || null);
            setForm((f) => {
              const manufacturer = model.manufacturer || r.manufacturer || f.manufacturer;
              const interval = model.inspection_interval_months || r.inspection_interval_months;
              const patRequired = model.pat_required ?? r.pat_required;
              return {
                ...f,
                model_id: model.id,
                name: f.name?.trim() ? f.name : [manufacturer, model.name].filter(Boolean).join(' '),
                manufacturer,
                category_id: model.category_id || r.category_id || f.category_id,
                asset_type: model.asset_type || f.asset_type,
                part_number: model.part_number || r.part_number || f.part_number,
                manual_link: model.manual_link || r.manual_link || f.manual_link,
                serial_number: plate?.serial_number || f.serial_number,
                manufacture_date: /^\d{4}$/.test(String(plate?.year_of_manufacture || ''))
                  ? `${plate.year_of_manufacture}-01-01`
                  : f.manufacture_date,
                useful_life_years: model.useful_life_years ?? f.useful_life_years,
                next_inspection_date: interval ? addMonths(interval) : f.next_inspection_date,
                next_pat_test_date: patRequired && !f.next_pat_test_date
                  ? addMonths(interval || 12)
                  : f.next_pat_test_date,
              };
            });
            toast.success(t('inv.nameplate.appliedToAsset'));
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default AssetForm;
